import React, { useState } from "react";
import Editor from "@monaco-editor/react";
import toast from "react-hot-toast";

interface JsomPromptMatchMakingModalProps {
  isOpen: boolean;
  title?: string;
  prompt: string | object;
  onClose: () => void;
  onSubmit:  (response: string) => Promise<boolean>;
  onSkip: () => void;
}

const JsomPromptMatchMakingModal: React.FC<JsomPromptMatchMakingModalProps> = ({
  isOpen,
  title = "Provide Your Response",
  prompt,
  onClose,
  onSubmit,
  onSkip
}) => {
  const [input, setInput] = useState("");
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (value: string | undefined) => {
    setInput(value || "");
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(
      typeof prompt === "string" ? prompt : JSON.stringify(prompt, null, 2)
    );
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  const handleSubmit = async () => {
    setLoading(true);
    if (input.trim() === "") {
      toast.error("Please provide a response.");
      setTimeout(() => setLoading(false), 1000);
      return;
    }

    try {
      JSON.parse(input);
      const success = await onSubmit(input); // Capture result
      if (success === true) {
        setInput(""); // ✅ Clear input only on success
        // toast.success("Submitted successfully!");
      }
      // toast.success("Submitted successfully!");
    } catch (err) {
      toast.error("Invalid JSON format.");
    }

    setTimeout(() => setLoading(false), 1000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl w-full max-w-6xl shadow-2xl text-white p-6 sm:p-8 overflow-y-auto max-h-[95vh]">
        {/* Header */}
        <div className="flex justify-between items-center mb-4 border-b border-white/20 pb-2">
          <h2 className="text-xl font-semibold text-yellow-300">{title}</h2>
          <button
            onClick={onClose}
            className="text-2xl text-red-300 hover:text-red-500 transition"
          >
            &times;
          </button>
        </div>

        {/* Prompt Preview */}
        <div className="relative mb-4">
          <button
            onClick={handleCopy}
            className={`absolute top-2 right-6 text-xs px-3 py-1 rounded bg-white/20 text-white hover:bg-white/30 transition ${
              copied ? "text-green-400 font-semibold" : ""
            }`}
          >
            {copied ? "✅ Copied!" : "Copy"}
          </button>
          <pre className="bg-white/10 text-sm p-4 rounded max-h-48 overflow-y-auto whitespace-pre-wrap border border-white/10">
            {typeof prompt === "string"
              ? prompt
              : JSON.stringify(prompt, null, 2)}
          </pre>
        </div>

        {/* Monaco Editor */}
        <Editor
          height="300px"
          defaultLanguage="json"
          value={input}
          onChange={handleChange}
          theme="vs-dark"
          options={{
            minimap: { enabled: false },
            fontSize: 14,
            formatOnPaste: true,
            formatOnType: true,
          }}
        />

        {/* Footer Buttons */}
        <div className="flex justify-end mt-6 space-x-4">
          <button
            onClick={onSkip}
            className="px-4 py-2 text-sm rounded bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm border border-white/20 text-white transition"
          >
            Skip to Next
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded bg-white/20 hover:bg-white/30 text-white transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`px-4 py-2 text-sm rounded text-white font-semibold ${
              loading
                ? "bg-blue-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {loading ? (
              <svg
                className="animate-spin h-4 w-4 mx-auto"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            ) : (
              "Save Response"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default JsomPromptMatchMakingModal;
